import { getCountryFlag } from '@/pages/home/_config/countries';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';
import { getPlatformIcon } from '@/pages/home/_config/platform';
import type {
  ChartConfig,
  EChartsOption,
  SeriesConfig,
  TLegendIconSource,
  TLegendLineSeriesLike,
} from './types';

export type { TLegendIconSource } from './types';

const mapCompositeKeyToSource = (key: string): TLegendIconSource => {
  const k = String(key || '').trim().toLowerCase();
  if (
    k === 'system' ||
    k === 'platform' ||
    k === 'os_list' ||
    k === 'system_list'
  ) {
    return 'system';
  }
  if (
    k === 'country' ||
    k === 'market' ||
    k === 'region' ||
    k === 'zone'
  ) {
    return 'country';
  }
  return 'ua_network';
};

/**
 * 与维度趋势 selectorMapKey 对齐，供未显式配置 legendIconSource 时推断
 */
export const inferLegendIconSourceFromSelectorMapKey = (
  mapKey: string,
): TLegendIconSource => mapCompositeKeyToSource(mapKey);

const resolveOneIcon = (code: string, source: TLegendIconSource): string => {
  const c = String(code || '').trim();
  if (!c) {
    return '';
  }
  if (source === 'system') {
    return getPlatformIcon(c);
  }
  if (source === 'country') {
    return getCountryFlag(c === 'Total' ? 'Global' : c);
  }
  const channelIcon = getMediaChannelIcon(c);
  return channelIcon === mediaChannelMap._default ? Default : channelIcon;
};

/**
 * 按 dimRawCode + 维度类型解析图例小图标 URL（可多个，如 system_country 组合）
 */
function resolveLegendIconUrlsForSeries(
  cfg: SeriesConfig | undefined,
  chartConfig: ChartConfig | undefined,
): string[] {
  if (!chartConfig?.showLegendDimensionIcons) {
    return [];
  }
  if (!cfg || (cfg as { suppressLegendDimensionIcon?: boolean }).suppressLegendDimensionIcon) {
    return [];
  }
  const raw = cfg.dimRawCode;
  if (typeof raw !== 'string' || !raw.trim()) {
    /** 无维度原始码（如上周同期、异动说明类系列）不展示，避免误伤异动相关图例 */
    return [];
  }
  const dimRaw = raw.trim();
  const composite = chartConfig.legendIconCompositeKeys;
  const primarySource = chartConfig.legendIconSource;

  if (
    Array.isArray(composite) &&
    composite.length === 2 &&
    dimRaw.includes('_')
  ) {
    const idx = dimRaw.indexOf('_');
    const left = dimRaw.slice(0, idx).trim();
    const right = dimRaw.slice(idx + 1).trim();
    if (left && right) {
      const s0 = mapCompositeKeyToSource(composite[0]);
      const s1 = mapCompositeKeyToSource(composite[1]);
      const a = resolveOneIcon(left, s0);
      const b = resolveOneIcon(right, s1);
      return [a, b].filter(Boolean);
    }
  }

  if (!primarySource) {
    return [];
  }
  const one = resolveOneIcon(dimRaw, primarySource);
  return one ? [one] : [];
}

/**
 * 在 ECharts 图例色点与文案之间插入维度图标（rich text）；仅处理带 dimRawCode 的 series。
 */
export function applyLegendDimensionIcons(
  option: EChartsOption,
  params: {
    lineSeries: TLegendLineSeriesLike[];
    seriesConfig: SeriesConfig[];
    chartConfig?: ChartConfig;
  },
): void {
  const { lineSeries, seriesConfig, chartConfig } = params;
  if (!chartConfig?.showLegendDimensionIcons) {
    return;
  }

  const dataSeries =
    option.series?.filter(
      s => s?.name && String(s.name) !== 'TopTipsEvent',
    ) ?? [];

  const filteredLineSeries = lineSeries.filter(
    s => s?.name && String(s.name) !== 'TopTipsEvent',
  );
  if (dataSeries.length !== filteredLineSeries.length || dataSeries.length === 0) {
    return;
  }

  const prevTs = option.legend?.textStyle;
  const prevRich =
    prevTs &&
    typeof prevTs === 'object' &&
    !Array.isArray(prevTs) &&
    typeof (prevTs as { rich?: unknown }).rich === 'object' &&
    (prevTs as { rich?: object }).rich !== null
      ? { ...(prevTs as { rich: object }).rich }
      : {};

  const rich: Record<string, unknown> = { ...prevRich };
  let richIdx = 0;
  const nameToPattern = new Map<string, string>();
  let anyIcon = false;

  dataSeries.forEach((s, i) => {
    const name = String(s.name);
    const urls = resolveLegendIconUrlsForSeries(seriesConfig[i], chartConfig);
    if (urls.length === 0) {
      nameToPattern.set(name, name);
      return;
    }
    anyIcon = true;
    let pattern = '';
    urls.forEach(url => {
      const key = `dbdLg${richIdx}`;
      richIdx += 1;
      rich[key] = {
        height: 13,
        // width: 14,
        backgroundColor: { image: url },
        verticalAlign: 'middle',
      };
      pattern += `{${key}|} `;
    });
    pattern += name;
    nameToPattern.set(name, pattern);
  });

  if (!anyIcon) {
    return;
  }

  const baseTs =
    prevTs && typeof prevTs === 'object' && !Array.isArray(prevTs)
      ? { ...prevTs }
      : {};

  const nextLegend = {
    ...option.legend,
    formatter: (legendName: string) =>
      nameToPattern.get(legendName) ?? legendName,
    textStyle: {
      ...baseTs,
      color: (baseTs as { color?: string }).color ?? '#838697',
      fontSize: (baseTs as { fontSize?: number }).fontSize ?? 12,
      lineHeight: (baseTs as { lineHeight?: number }).lineHeight ?? 16,
      rich,
    },
  };
  Object.assign(option, { legend: nextLegend });
}
